import { Col, Divider, Input, Row } from "antd";
import React, { useEffect, useRef } from "react";
import ZTypography from "@atoms/ZTypography";
import { EFontWeights } from "@atoms/ZTypography/interface";
import Form from "@ant-design/compatible/lib/form";
import ZInputNumber, {
	InputNumberWidths
} from "@atoms/ZInputNumber";
import { getCurrencySettings, getEntityCurrencyCode } from "@/Utils/Currency";
import { valueType } from "antd/lib/statistic/utils";
import {
	IOpportunityAdditionalFieldsProps,
	ZInputComponentProps
} from "./interface";
import {
	calculateManualTotalRevenueFields,
	convertTextToMergeTag
} from "@/Utils";
import ZEditorMergeTag from "@molecules/ZMergeTag/ZEditorMergetag";
import { MergeTagVariableDataType } from "@/Contracts/Documents";
import "./index.less";
import { ZomentumWindow } from "@/Contracts";

declare let window: ZomentumWindow;

const ZOppRevenueAndCostFields: React.FC<
	IOpportunityAdditionalFieldsProps
> = props => {
	const mounted = useRef<boolean>(false);
	useEffect(() => {
		mounted.current = true;
		if (mounted.current) {
			props?.onMountedCallack && props.onMountedCallack();
		}
		return () => {
			mounted.current = false;
		};
	}, []);
	const oppCurrency =
		props.form?.getFieldValue?.("currency") ||
		getEntityCurrencyCode(props.currentActiveOpportunity) ||
		getCurrencySettings().currency;
	return (
		<Row className="bg-gray-2 min-w-full mb-7">
			<Col span={12} className="border-right border-gray-5">
				<ZTypography.ZText
					level={2}
					className="text-center block mb-5 p-3 bg-gray-3"
					weight={EFontWeights.MEDIUM}
					data-testid="cost-field"
				>
					Cost
				</ZTypography.ZText>

				<ZOppRevenueAndCostInput
					shouldShowExtraFields={props.shouldShowExtraFields}
					monthsToCalculateTotal={props.monthsToCalculateTotal}
					label="One time cost"
					fieldDecorater={"manual_total.one_time_cost"}
					initialValue={
						props.isFormInEditOpportunity
							? props.shouldUseDocumentTotal
								? props.currentActiveOpportunity?.total
										?.one_time_cost
								: props.currentActiveOpportunity?.manual_total
										?.one_time_cost
							: 0
					}
					form={props.form}
					currency={oppCurrency}
					disabled={props.shouldUseDocumentTotal}
					minimumValue={0}
					addOnAfter={true}
					formName={props.formName}
					mergeTags={props.mergeTags}
					dataTestId="one-time-cost"
				/>

				<ZOppRevenueAndCostInput
					shouldShowExtraFields={props.shouldShowExtraFields}
					monthsToCalculateTotal={props.monthsToCalculateTotal}
					label="Weekly cost"
					fieldDecorater={"manual_total.weekly_cost"}
					initialValue={
						props.isFormInEditOpportunity
							? props.shouldUseDocumentTotal
								? props.currentActiveOpportunity?.total
										?.weekly_cost
								: props.currentActiveOpportunity?.manual_total
										?.weekly_cost
							: 0
					}
					form={props.form}
					currency={oppCurrency}
					disabled={props.shouldUseDocumentTotal}
					minimumValue={0}
					addOnAfter={true}
					formName={props.formName}
					mergeTags={props.mergeTags}
				/>

				<ZOppRevenueAndCostInput
					shouldShowExtraFields={props.shouldShowExtraFields}
					monthsToCalculateTotal={props.monthsToCalculateTotal}
					label="Monthly cost"
					fieldDecorater={"manual_total.monthly_cost"}
					initialValue={
						props.isFormInEditOpportunity
							? props.shouldUseDocumentTotal
								? props.currentActiveOpportunity?.total
										?.monthly_cost
								: props.currentActiveOpportunity?.manual_total
										?.monthly_cost
							: 0
					}
					form={props.form}
					currency={oppCurrency}
					disabled={props.shouldUseDocumentTotal}
					minimumValue={0}
					addOnAfter={true}
					formName={props.formName}
					mergeTags={props.mergeTags}
				/>

				<ZOppRevenueAndCostInput
					shouldShowExtraFields={props.shouldShowExtraFields}
					monthsToCalculateTotal={props.monthsToCalculateTotal}
					label="Quarterly cost"
					fieldDecorater={"manual_total.quarterly_cost"}
					initialValue={
						props.isFormInEditOpportunity
							? props.shouldUseDocumentTotal
								? props.currentActiveOpportunity?.total
										?.quarterly_cost
								: props.currentActiveOpportunity?.manual_total
										?.quarterly_cost
							: 0
					}
					form={props.form}
					currency={oppCurrency}
					disabled={props.shouldUseDocumentTotal}
					minimumValue={0}
					addOnAfter={true}
					formName={props.formName}
					mergeTags={props.mergeTags}
				/>

				<ZOppRevenueAndCostInput
					shouldShowExtraFields={props.shouldShowExtraFields}
					monthsToCalculateTotal={props.monthsToCalculateTotal}
					label="Semi-Annually cost"
					fieldDecorater={"manual_total.semi_annually_cost"}
					initialValue={
						props.isFormInEditOpportunity
							? props.shouldUseDocumentTotal
								? props.currentActiveOpportunity?.total
										?.semi_annually_cost
								: props.currentActiveOpportunity?.manual_total
										?.semi_annually_cost
							: 0
					}
					form={props.form}
					currency={oppCurrency}
					disabled={props.shouldUseDocumentTotal}
					minimumValue={0}
					addOnAfter={true}
					formName={props.formName}
					mergeTags={props.mergeTags}
				/>

				<ZOppRevenueAndCostInput
					shouldShowExtraFields={props.shouldShowExtraFields}
					monthsToCalculateTotal={props.monthsToCalculateTotal}
					label="Annually cost"
					fieldDecorater={"manual_total.annual_cost"}
					initialValue={
						props.isFormInEditOpportunity
							? props.shouldUseDocumentTotal
								? props.currentActiveOpportunity?.total
										?.annual_cost
								: props.currentActiveOpportunity?.manual_total
										?.annual_cost
							: 0
					}
					form={props.form}
					currency={oppCurrency}
					disabled={props.shouldUseDocumentTotal}
					minimumValue={0}
					addOnAfter={true}
					formName={props.formName}
					mergeTags={props.mergeTags}
				/>

				{props.shouldShowExtraFields && (
					<ZOppRevenueAndCostInput
						shouldShowExtraFields={props.shouldShowExtraFields}
						monthsToCalculateTotal={props.monthsToCalculateTotal}
						label="Total cost"
						fieldDecorater={"manual_total.total_cost"}
						initialValue={
							props.isFormInEditOpportunity
								? props.shouldUseDocumentTotal
									? props.currentActiveOpportunity?.total
											?.total_cost
									: props.currentActiveOpportunity
											?.manual_total?.total_cost
								: 0
						}
						form={props.form}
					currency={oppCurrency}
						disabled={true}
						minimumValue={0}
						addOnAfter={true}
						dataTestId="total-cost"
						className="font-bold"
						formName={props.formName}
						mergeTags={props.mergeTags}
					/>
				)}
				<Divider />
			</Col>
			<Col span={12}>
				<ZTypography.ZText
					level={2}
					className="text-center block p-3 bg-gray-3 mb-5"
					weight={EFontWeights.MEDIUM}
					data-testid="revenue-field"
				>
					Revenue
				</ZTypography.ZText>

				<ZOppRevenueAndCostInput
					shouldShowExtraFields={props.shouldShowExtraFields}
					monthsToCalculateTotal={props.monthsToCalculateTotal}
					label="One time revenue"
					fieldDecorater={"manual_total.one_time"}
					initialValue={
						props.isFormInEditOpportunity
							? props.shouldUseDocumentTotal
								? props.currentActiveOpportunity?.total
										?.one_time
								: props.currentActiveOpportunity?.manual_total
										?.one_time
							: 0
					}
					form={props.form}
					currency={oppCurrency}
					disabled={props.shouldUseDocumentTotal}
					minimumValue={0}
					addOnAfter={true}
					dataTestId="one-time-revenue"
					formName={props.formName}
					mergeTags={props.mergeTags}
				/>

				<ZOppRevenueAndCostInput
					shouldShowExtraFields={props.shouldShowExtraFields}
					monthsToCalculateTotal={props.monthsToCalculateTotal}
					label="Weekly revenue"
					fieldDecorater={"manual_total.weekly"}
					initialValue={
						props.isFormInEditOpportunity
							? props.shouldUseDocumentTotal
								? props.currentActiveOpportunity?.total?.weekly
								: props.currentActiveOpportunity?.manual_total
										?.weekly
							: 0
					}
					form={props.form}
					currency={oppCurrency}
					disabled={props.shouldUseDocumentTotal}
					minimumValue={0}
					addOnAfter={true}
					formName={props.formName}
					mergeTags={props.mergeTags}
				/>

				<ZOppRevenueAndCostInput
					shouldShowExtraFields={props.shouldShowExtraFields}
					monthsToCalculateTotal={props.monthsToCalculateTotal}
					label="Monthly revenue"
					fieldDecorater={"manual_total.monthly"}
					initialValue={
						props.isFormInEditOpportunity
							? props.shouldUseDocumentTotal
								? props.currentActiveOpportunity?.total?.monthly
								: props.currentActiveOpportunity?.manual_total
										?.monthly
							: 0
					}
					form={props.form}
					currency={oppCurrency}
					disabled={props.shouldUseDocumentTotal}
					minimumValue={0}
					addOnAfter={true}
					formName={props.formName}
					mergeTags={props.mergeTags}
				/>
				<ZOppRevenueAndCostInput
					shouldShowExtraFields={props.shouldShowExtraFields}
					monthsToCalculateTotal={props.monthsToCalculateTotal}
					label="Quarterly revenue"
					fieldDecorater={"manual_total.quarterly"}
					initialValue={
						props.isFormInEditOpportunity
							? props.shouldUseDocumentTotal
								? props.currentActiveOpportunity?.total
										?.quarterly
								: props.currentActiveOpportunity?.manual_total
										?.quarterly
							: 0
					}
					form={props.form}
					currency={oppCurrency}
					disabled={props.shouldUseDocumentTotal}
					minimumValue={0}
					addOnAfter={true}
					formName={props.formName}
					mergeTags={props.mergeTags}
				/>

				<ZOppRevenueAndCostInput
					shouldShowExtraFields={props.shouldShowExtraFields}
					monthsToCalculateTotal={props.monthsToCalculateTotal}
					label="Semi-Annually revenue"
					fieldDecorater={"manual_total.semi_annually"}
					initialValue={
						props.isFormInEditOpportunity
							? props.shouldUseDocumentTotal
								? props.currentActiveOpportunity?.total
										?.semi_annually
								: props.currentActiveOpportunity?.manual_total
										?.semi_annually
							: 0
					}
					form={props.form}
					currency={oppCurrency}
					disabled={props.shouldUseDocumentTotal}
					minimumValue={0}
					addOnAfter={true}
					formName={props.formName}
					mergeTags={props.mergeTags}
				/>

				<ZOppRevenueAndCostInput
					shouldShowExtraFields={props.shouldShowExtraFields}
					monthsToCalculateTotal={props.monthsToCalculateTotal}
					label="Annually revenue"
					fieldDecorater={"manual_total.annual"}
					initialValue={
						props.isFormInEditOpportunity
							? props.shouldUseDocumentTotal
								? props.currentActiveOpportunity?.total?.annual
								: props.currentActiveOpportunity?.manual_total
										?.annual
							: 0
					}
					form={props.form}
					currency={oppCurrency}
					disabled={props.shouldUseDocumentTotal}
					minimumValue={0}
					addOnAfter={true}
					formName={props.formName}
					mergeTags={props.mergeTags}
				/>

				{props.shouldShowExtraFields && (
					<ZOppRevenueAndCostInput
						shouldShowExtraFields={props.shouldShowExtraFields}
						monthsToCalculateTotal={props.monthsToCalculateTotal}
						label="Total revenue"
						fieldDecorater={"manual_total.total_revenue"}
						initialValue={
							props.isFormInEditOpportunity
								? props.shouldUseDocumentTotal
									? props.currentActiveOpportunity?.total
											?.total_revenue
									: props.currentActiveOpportunity
											?.manual_total?.total_revenue
								: 0
						}
						form={props.form}
					currency={oppCurrency}
						disabled={true}
						minimumValue={0}
						addOnAfter={true}
						dataTestId="total-revenue"
						className="font-bold"
						formName={props.formName}
						mergeTags={props.mergeTags}
					/>
				)}
				<Divider />
			</Col>
			{props.shouldShowExtraFields && (
				<>
					<Col span={12}></Col>
					<Col span={12}>
						<ZOppRevenueAndCostInput
							shouldShowExtraFields={props.shouldShowExtraFields}
							monthsToCalculateTotal={
								props.monthsToCalculateTotal
							}
							label="Profit margin"
							fieldDecorater={"manual_total.margin_value"}
							initialValue={
								props.isFormInEditOpportunity
									? props.shouldUseDocumentTotal
										? props.currentActiveOpportunity?.total
												?.margin_value
										: props.currentActiveOpportunity
												?.manual_total?.margin_value
									: 0
							}
							form={props.form}
					currency={oppCurrency}
							disabled={true}
							addOnAfter={true}
							dataTestId="profit-margin"
							className="mt-4"
							formName={props.formName}
							mergeTags={props.mergeTags}
						/>
					</Col>
				</>
			)}
		</Row>
	);
};

type ZOppRevenueAndCostInputProps = ZInputComponentProps & {
	readonly currency?: string;
};

const ZOppRevenueAndCostInput: React.FC<ZOppRevenueAndCostInputProps> = props => {
	const monthsToCalculateTotal = props.monthsToCalculateTotal ?? 1;
	const checkForMergeTags = () => {
		return [
			"one_time",
			"weekly",
			"monthly",
			"quarterly",
			"semi_annually",
			"annual",
			"one_time_cost",
			"weekly_cost",
			"monthly_cost",
			"quarterly_cost",
			"semi_annually_cost",
			"annual_cost"
		].some(field => {
			const value = props.form.getFieldValue(
				`manual_total.${field}`
			) as string;
			return value?.toString().includes("{{");
		});
	};
	const handleDisabledValues = async (e: valueType) => {
		await props.form.setFieldsValue({
			e
		});

		const hasMergeTags = checkForMergeTags();

		if (props.mergeTags && hasMergeTags) {
			props.form.setFieldsValue({
				"manual_total.margin_value": "--",
				"manual_total.total_revenue": "--",
				"manual_total.total_cost": "--"
			});
		} else {
			/* calculating it after we set the changed field */
			const {
				manualTotalMarginValue,
				manualTotalTotalCostFieldValue,
				manualTotalTotalRevenueFieldValue
			} = calculateManualTotalRevenueFields(
				props.form,
				monthsToCalculateTotal
			);

			props.form.setFieldsValue({
				"manual_total.margin_value": manualTotalMarginValue,
				"manual_total.total_revenue": manualTotalTotalRevenueFieldValue,
				"manual_total.total_cost": manualTotalTotalCostFieldValue
			});
		}

		if (props.mergeTags) {
			["margin_value", "total_revenue", "total_cost"].forEach(field => {
				const editor = window?.tinyMCE?.get?.(
					`${props.formName}_manual_total-${field}`
				);
				const value =
					props.form.getFieldValue(`manual_total.${field}`) ??
					(hasMergeTags ? "--" : "0");
				editor?.setContent("" + value);
			});
		}
	};

	const editorId = `${props.formName}_${props.fieldDecorater.replace(
		".",
		"-"
	)}`;

	const setEditorValue = () => {
		if (props.mergeTags) {
			const editor = window?.tinyMCE?.get?.(editorId);
			const value = props.form.getFieldValue(props.fieldDecorater) ?? "0";
			editor?.setContent(
				convertTextToMergeTag(props.mergeTags, "" + value, true)
			);
		}
	};

	const InputField =
		props.mergeTags && !props.disabled ? (
			<Input.Group>
				{props.form.getFieldDecorator(props.fieldDecorater)(
					<ZEditorMergeTag
						id={editorId}
						MergeTags={props.mergeTags ?? []}
						border={true}
						className={`edit-opportunity-cost-revenue-field has-currency-addon ${
							props.disabled ? "disabled-field" : ""
						}`}
						data_type={MergeTagVariableDataType.DOUBLE}
						onChange={(e: valueType) => handleDisabledValues(e)}
						data-testid={props.dataTestId}
						disabled={props.disabled}
						showInitialLoading={false}
						handleInit={setEditorValue}
					/>
				)}
				<Input
					placeholder={props.currency ?? getCurrencySettings().currency}
					disabled={true}
					className="currency-addon cost-revenue-field-currency-addon"
				/>
			</Input.Group>
		) : (
			props.form.getFieldDecorator(props.fieldDecorater, {
				initialValue: props.initialValue
			})(
				props.shouldShowExtraFields ? (
					<ZInputNumber
						min={props.minimumValue}
						width={InputNumberWidths.lg}
						data-testid={props.dataTestId}
						addOnAfter={
							props.addOnAfter
								? props.currency ??
								  getCurrencySettings().currency
								: ""
						}
						disabled={props.disabled}
						onChange={(e: valueType) => handleDisabledValues(e)}
						className="justify-end"
						step={0.01}
					/>
				) : (
					<ZInputNumber
						min={props.minimumValue}
						width={InputNumberWidths.lg}
						data-testid={props.dataTestId}
						addOnAfter={
							props.addOnAfter
								? props.currency ??
								  getCurrencySettings().currency
								: ""
						}
						disabled={props.disabled}
						className="justify-end"
						step={0.01}
					/>
				)
			)
		);

	return (
		<Form.Item
			label={props.label}
			labelCol={{ span: props.labelCol ? props.labelCol : 12 }}
			wrapperCol={{ span: props.wrapperCol ? props.wrapperCol : 12 }}
			labelAlign="left"
			colon={false}
			className={`${props.className} px-4`}
		>
			{InputField}
		</Form.Item>
	);
};

export default ZOppRevenueAndCostFields;
